/**
 * Gestor Agent Service — Strategic Performance Director
 * 
 * Endpoints:
 *   GET  /api/gestor/dashboard
 *   GET  /api/gestor/plans
 *   POST /api/gestor/plans
 *   GET  /api/gestor/plans/:id
 *   POST /api/gestor/plans/:id/activate
 *   GET  /api/gestor/plans/:id/directives
 *   GET  /api/gestor/plans/:id/tasks
 *   GET  /api/gestor/plans/:id/decisions
 *   GET  /api/gestor/plans/:id/exceptions
 *   GET  /api/gestor/plans/:id/alignment
 *   GET  /api/gestor/methodology/active
 *   POST /api/gestor/decisions
 *   POST /api/gestor/exceptions/:id/decide
 */
import { AZURE } from './api';

// ─── API Adapters (real API calls, empty fallback on error) ──────────────────

/**
 * Dashboard — plano ativo + métricas consolidadas.
 *
 * `tenantId` é OBRIGATÓRIO. Default removido (jun/2026): o default 1 derivava
 * silenciosamente para a casa VUKZ, gerando 403 em Treinadores/Preps que caíam
 * indiretamente nesta função via páginas do /manager/*. Falha honesta agora.
 */
export const getGestorDashboard = async (tenantId) => {
  if (!tenantId) {
    console.warn('[gestorService] getGestorDashboard chamado sem tenantId — abortando (sem derivação para 1).');
    return null;
  }
  try {
    const { data } = await AZURE.get('/gestor/dashboard', { params: { tenant_id: tenantId } });
    return data.data || null;
  } catch (err) {
    console.error('[gestorService] getGestorDashboard error:', err);
    return null;
  }
};

/**
 * Diretrizes de categoria do plano ativo
 */
export const getPlanDirectives = async (planId) => {
  try {
    const { data } = await AZURE.get(`/gestor/plans/${planId}/directives`);
    return data.data || [];
  } catch (err) {
    console.error('[gestorService] getPlanDirectives error:', err);
    return [];
  }
};

/**
 * Decision log do plano ativo
 */
export const getPlanDecisions = async (planId) => {
  try {
    const { data } = await AZURE.get(`/gestor/plans/${planId}/decisions`);
    return data.data || [];
  } catch (err) {
    console.error('[gestorService] getPlanDecisions error:', err);
    return [];
  }
};

/**
 * Execution tasks do plano ativo
 */
export const getPlanTasks = async (planId) => {
  try {
    const { data } = await AZURE.get(`/gestor/plans/${planId}/tasks`);
    return data.data || [];
  } catch (err) {
    console.error('[gestorService] getPlanTasks error:', err);
    return [];
  }
};

/**
 * Exceções pendentes
 */
export const getPlanExceptions = async (planId) => {
  try {
    const { data } = await AZURE.get(`/gestor/plans/${planId}/exceptions`);
    return data.data || [];
  } catch (err) {
    console.error('[gestorService] getPlanExceptions error:', err);
    return [];
  }
};

/**
 * Registrar decisão
 */
export const logDecision = async (decision) => {
  const { data } = await AZURE.post('/gestor/decisions', decision);
  return data;
};

/**
 * Decidir exceção (approve/deny)
 */
export const decideException = async (excId, decision) => {
  const { data } = await AZURE.post(`/gestor/exceptions/${excId}/decide`, decision);
  return data;
};

/**
 * Ativar plano
 */
export const activatePlan = async (planId, approvedBy = 'Gestor') => {
  const { data } = await AZURE.post(`/gestor/plans/${planId}/activate`, { approved_by: approvedBy });
  return data;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const PHASE_COLORS = {
  Base: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  Build: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  Peak: { bg: '#fce7f3', text: '#9d174d', dot: '#ec4899' },
  Taper: { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
};

export const AGENT_LABELS = {
  coach: 'Treinador',
  prep: 'Prep. Física',
  physio: 'Fisioterapia',
  nutri: 'Nutrição',
  agenda: 'Agenda',
  medical: 'Médico',
};

export const DIRECTIVE_STATUS = {
  draft: { label: 'Rascunho', bg: '#f3f4f6', text: '#374151' },
  approved: { label: 'Aprovado', bg: '#d1fae5', text: '#065f46' },
  needs_adjustment: { label: 'Ajustar', bg: '#fef3c7', text: '#92400e' },
  exception: { label: 'Exceção', bg: '#fce7f3', text: '#9d174d' },
};

export const DECISION_TYPE_LABELS = {
  strategic: 'Estratégica',
  tactical: 'Tática',
  exception: 'Exceção',
  methodology: 'Metodologia',
  tradeoff: 'Tradeoff',
  correction: 'Correção',
};

export const DECISION_TYPE_COLORS = {
  strategic: { bg: '#dbeafe', text: '#1e40af' },
  tactical: { bg: '#e0e7ff', text: '#3730a3' },
  exception: { bg: '#fce7f3', text: '#9d174d' },
  methodology: { bg: '#d1fae5', text: '#065f46' },
  tradeoff: { bg: '#fef3c7', text: '#92400e' },
  correction: { bg: '#fee2e2', text: '#991b1b' },
};
